import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const TILE_URL = process.env.REACT_APP_MAP_TILE_URL ||
  "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

const COL = { available: "#34d399", mission: "#f59e0b", idle: "#778698", selected: "#22d3ee" };

const toMs = (iso) => (iso ? Date.parse(iso) : null);

// CENTER — VORTEX Live World. Leaflet (moteur), tuiles configurables via REACT_APP_MAP_TILE_URL.
export default function LiveWorldMap({ resources, selectedVehicle, onSelectVehicle, replay, tMs }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const fitKeyRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: true })
      .setView([46.83, 6.92], 10);
    L.tileLayer(TILE_URL, { maxZoom: 19, attribution: "© OpenStreetMap" }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(elRef.current);
    return () => { ro.disconnect(); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, lg = layerRef.current;
    if (!map || !lg) return;
    lg.clearLayers();
    const bounds = [];
    const list = resources || [];

    list.forEach((r) => {
      if (typeof r.lat !== "number" || typeof r.lon !== "number") return;
      const sel = r.id === selectedVehicle;
      const color = r.status === "AVAILABLE" ? COL.available : (r.current_mission ? COL.mission : COL.idle);
      if (sel) {
        L.circleMarker([r.lat, r.lon], { radius: 15, color: COL.selected, weight: 2,
          fillColor: COL.selected, fillOpacity: 0.12 }).addTo(lg);
      }
      const batt = r.battery === "DATA UNAVAILABLE" ? "N/D" : `${r.battery?.level ?? "?"}%`;
      const nxt = r.current_mission?.next_stop;
      L.circleMarker([r.lat, r.lon], {
        radius: sel ? 9 : 7, color: sel ? "#fff" : "#06090d", weight: sel ? 2 : 2,
        fillColor: color, fillOpacity: 0.96,
      })
        .bindTooltip(r.name || r.vehicle_id || r.id, { direction: "top", offset: [0, -6] })
        .bindPopup(
          `<b>${r.name || r.id}</b><br/>${r.status}` +
          `<br/>vitesse: ${r.speed_kmh ?? "?"} km/h · batt: ${batt}` +
          (r.current_mission ? `<br/>mission ${r.current_mission.state}` : "") +
          (nxt && typeof nxt.lat === "number" ? `<br/>prochain arrêt: ${nxt.label || nxt.name || "—"}` : "") +
          `<br/><span style="color:#778698">GPS: ${(r.pos_reading_at || "n/d").slice(0, 19).replace("T", " ")}</span>`)
        .on("click", () => onSelectVehicle && onSelectVehicle(r.id))
        .addTo(lg);
      bounds.push([r.lat, r.lon]);

      // PLANNED (fin / pointillé) — arrêts de la mission courante (véhicule sélectionné)
      if (sel && r.current_mission?.stops) {
        const stops = r.current_mission.stops
          .filter((s) => s && typeof s.lat === "number" && typeof s.lon === "number")
          .map((s) => [s.lat, s.lon]);
        if (stops.length > 1) {
          L.polyline(stops, { color: COL.selected, weight: 1.5, dashArray: "3,7", opacity: 0.85 }).addTo(lg);
          stops.forEach((p) => L.circleMarker(p, { radius: 3, color: COL.selected, weight: 1, fillColor: "#06090d", fillOpacity: 1 }).addTo(lg));
        }
      }

      // ACTUAL (plein) — trace GPS live courte
      if (!replay && r.actual_path && r.actual_path.length > 1) {
        L.polyline(r.actual_path.map((p) => [p.lat, p.lon]), { color: COL.mission, weight: 3, opacity: sel ? 0.95 : 0.6 }).addTo(lg);
      }
    });

    // HISTORICAL REPLAY — trace du véhicule sélectionné (atténuée) + portion jusqu'à T (accentuée)
    if (replay && replay.points && replay.points.length > 1) {
      const pts = replay.points;
      L.polyline(pts.map((p) => [p.lat, p.lon]), { color: "#3a4a5c", weight: 2, opacity: 0.55 }).addTo(lg);
      const upto = tMs ? pts.filter((p) => toMs(p.at) <= tMs) : pts;
      if (upto.length > 1) {
        L.polyline(upto.map((p) => [p.lat, p.lon]), { color: COL.selected, weight: 3.5, opacity: 0.95 }).addTo(lg);
      }
      const cur = upto[upto.length - 1] || pts[0];
      if (cur) {
        L.circleMarker([cur.lat, cur.lon], { radius: 8, color: "#fff", weight: 2, fillColor: COL.selected, fillOpacity: 1, className: "pulse" }).addTo(lg);
      }
      pts.forEach((p) => bounds.push([p.lat, p.lon]));
    }

    if (bounds.length >= 1) {
      const key = `${selectedVehicle || ""}|${replay ? "r" : "l"}|${bounds.length}`;
      if (fitKeyRef.current !== key) {
        fitKeyRef.current = key;
        if (bounds.length >= 2) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
        else map.setView(bounds[0], 13);
      }
    }
  }, [resources, selectedVehicle, replay, tMs, onSelectVehicle]);

  const noLive = resources && resources.length === 0 && !replay;

  return (
    <section className="ck-panel" data-testid="ck-map-panel">
      <div className="ck-panel__head">
        <h2>VORTEX Live World</h2>
        <span className="sub">{replay ? "REPLAY historique" : "temps réel"}{selectedVehicle ? " · véhicule sélectionné" : ""}</span>
      </div>
      <div className="ck-panel__body nopad">
        <div className="ck-map">
          <div ref={elRef} data-testid="cockpit-map" style={{ height: "100%" }} />
          {noLive && <div className="map-overlay" data-testid="map-nodata">NO LIVE DATA — aucune ressource GPS active</div>}
          <div className="map-legend">
            <b>Trajets</b>
            <span className="leg-row"><span className="leg-line" style={{ borderColor: "#22d3ee", borderStyle: "dashed" }} />prévu</span>
            <span className="leg-row"><span className="leg-line" style={{ borderColor: "#f59e0b" }} />réel</span>
            <span className="leg-row"><span className="leg-line" style={{ borderColor: "#3a4a5c" }} />replay</span>
            <span className="leg-row"><span className="leg-line" style={{ borderColor: "#22d3ee", borderTopWidth: 4 }} />jusqu&apos;à T</span>
          </div>
        </div>
      </div>
    </section>
  );
}
